import React, { useState } from 'react'
import Box from '@mui/material/Box'
import Alert from '@mui/material/Alert'
import Skeleton from '@mui/material/Skeleton'
import TablePagination from '@mui/material/TablePagination'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import palette from '../../../theme/palette'
import { statusBadgeSx } from '../../common/sharedStyles'
import { useGetChangelogQuery } from '../../redux/internalAlertsApiSlice'
import { shortTime } from './alertUtils'

const PAGE = 10

const addr = (m) => m?.mobile || m?.email || ''
const slot = (m) => (m?.email && m?.headerType && m.headerType !== 'TO' ? ` · ${m.headerType}` : '')
const who = (m) => (m?.displayName ? `${m.displayName} (${addr(m)}${slot(m)})` : (addr(m) ? `${addr(m)}${slot(m)}` : 'recipient'))
const ACTION = {
  RECIPIENT_ADDED: { tone: 'success', text: (e) => `Added ${who(e.after)}` },
  RECIPIENT_REACTIVATED: { tone: 'success', text: (e) => `Re-added ${who(e.after)}` },
  RECIPIENT_DEACTIVATED: { tone: 'failed', text: (e) => `Removed ${who(e.before)}` },
  ENABLED: { tone: 'success', text: () => 'Switched on' },
  DISABLED: { tone: 'warning', text: () => 'Switched off' },
  METADATA_UPDATED: { tone: 'info', text: (e) => `Changed ${Object.keys(e.after || {}).join(', ') || 'details'}` },
}
const fmt = (v) => (v === null || v === undefined ? '—' : typeof v === 'object' ? JSON.stringify(v) : String(v))

/** Who changed what on this alert, newest first — the answer to "who added Rudra, and when". */
export default function ChangeLogList({ templateName }) {
  const [page, setPage] = useState(0)
  const { data, isFetching, error } = useGetChangelogQuery({ templateName, page: page + 1, pageSize: PAGE }, { skip: !templateName })
  const rows = data?.rows || []

  if (error) return <Alert severity='error' sx={{ py: 0.5 }}>{error?.data?.message || error?.message || 'Could not load the change log.'}</Alert>
  if (isFetching && !data) return <Box>{[...Array(3)].map((_, i) => <Skeleton key={i} height={28} sx={{ mb: 0.5 }} />)}</Box>
  if (!rows.length) return <Typography variant='body2' sx={{ color: 'text.disabled' }}>No changes recorded yet.</Typography>

  return (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {rows.map(e => {
          const a = ACTION[e.action] || { tone: 'neutral', text: () => e.action }
          const detail = e.action === 'METADATA_UPDATED'
            ? Object.keys(e.after || {}).map(k => `${k}: ${fmt(e.before?.[k])} → ${fmt(e.after?.[k])}`).join('\n')
            : null
          return (
            <Box key={e.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 0.5, borderBottom: `1px solid ${palette.grey[200]}` }}>
              <Typography variant='caption' sx={{ color: 'text.disabled', whiteSpace: 'nowrap', pt: 0.25, minWidth: 118 }} title={`${e.at} IST`}>{shortTime(e.at)}</Typography>
              <Box component='span' sx={{ ...statusBadgeSx(a.tone), whiteSpace: 'nowrap', flexShrink: 0 }}>{e.action.replace(/_/g, ' ').toLowerCase()}</Box>
              <Box sx={{ minWidth: 0 }}>
                <Tooltip title={detail ? <Box component='span' sx={{ whiteSpace: 'pre-line', fontFamily: 'monospace', fontSize: 11 }}>{detail}</Box> : ''} arrow disableHoverListener={!detail}>
                  <Typography variant='body2' sx={{ fontWeight: 600 }}>{a.text(e)}</Typography>
                </Tooltip>
                <Typography variant='caption' sx={{ color: 'text.secondary', display: 'block' }}>
                  by {e.actor || 'unknown'}{e.reason ? <> · <Box component='span' sx={{ fontStyle: 'italic' }}>“{e.reason}”</Box></> : null}
                </Typography>
              </Box>
            </Box>
          )
        })}
      </Box>
      {(data?.total || 0) > PAGE && (
        <TablePagination component='div' count={data.total} page={page} rowsPerPage={PAGE} rowsPerPageOptions={[PAGE]} onPageChange={(_, p) => setPage(p)} />
      )}
    </Box>
  )
}
